/**
 * IndexedDB wrapper for structured data storage
 * Provides a simplified interface for database operations
 */
class IndexedDBWrapper {
    constructor(dbName, version = 1) {
        this.dbName = dbName;
        this.version = version;
        this.db = null;
    }

    /**
     * Initialize the database with object stores
     * @param {Array} stores - Array of store configurations
     * @returns {Promise<IDBDatabase>}
     */
    async init(stores = []) {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                stores.forEach(store => {
                    if (!db.objectStoreNames.contains(store.name)) {
                        const objectStore = db.createObjectStore(store.name, store.options || { keyPath: 'id' });

                        // Create indexes if specified
                        if (store.indexes) {
                            store.indexes.forEach(index => {
                                objectStore.createIndex(index.name, index.keyPath, index.options || {});
                            });
                        }
                    }
                });
            };
        });
    }

    /**
     * Add or update a record in the specified store
     * @param {string} storeName - Name of the object store
     * @param {Object} data - Data to store
     * @returns {Promise<any>}
     */
    async put(storeName, data) {
        return this._performTransaction(storeName, 'readwrite', (store) => {
            return store.put(data);
        });
    }

    /**
     * Get a record by key from the specified store
     * @param {string} storeName - Name of the object store
     * @param {any} key - Key to retrieve
     * @returns {Promise<any>}
     */
    async get(storeName, key) {
        return this._performTransaction(storeName, 'readonly', (store) => {
            return store.get(key);
        });
    }

    /**
     * Get all records from the specified store
     * @param {string} storeName - Name of the object store
     * @returns {Promise<Array>}
     */
    async getAll(storeName) {
        return this._performTransaction(storeName, 'readonly', (store) => {
            return store.getAll();
        });
    }

    /**
     * Delete a record by key from the specified store
     * @param {string} storeName - Name of the object store
     * @param {any} key - Key to delete
     * @returns {Promise<void>}
     */
    async delete(storeName, key) {
        return this._performTransaction(storeName, 'readwrite', (store) => {
            return store.delete(key);
        });
    }

    /**
     * Clear all records from the specified store
     * @param {string} storeName - Name of the object store
     * @returns {Promise<void>}
     */
    async clear(storeName) {
        return this._performTransaction(storeName, 'readwrite', (store) => {
            return store.clear();
        });
    }

    /**
     * Count records in the specified store
     * @param {string} storeName - Name of the object store
     * @returns {Promise<number>}
     */
    async count(storeName) {
        return this._performTransaction(storeName, 'readonly', (store) => {
            return store.count();
        });
    }

    /**
     * Query records using an index
     * @param {string} storeName - Name of the object store
     * @param {string} indexName - Name of the index
     * @param {any} query - Query value or IDBKeyRange
     * @returns {Promise<Array>}
     */
    async queryByIndex(storeName, indexName, query) {
        return this._performTransaction(storeName, 'readonly', (store) => {
            const index = store.index(indexName);
            return index.getAll(query);
        });
    }

    /**
     * Perform a transaction on the database
     * @private
     */
    _performTransaction(storeName, mode, operation) {
        return new Promise((resolve, reject) => {
            if (!this.db) {
                reject(new Error('Database not initialized'));
                return;
            }

            const transaction = this.db.transaction([storeName], mode);
            const store = transaction.objectStore(storeName);
            const request = operation(store);

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Close the database connection
     */
    close() {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
    }
}

export default IndexedDBWrapper;